import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { AppRemixIcon } from '@/layout/AppComponents';
import Moment from 'moment';
import ReactDOMServer from 'react-dom/server';
import colors from '../../../../../constants/colors';
import { axiosPath, path } from '../../../../../routing/helpers/router';
import { getGclFilterInitialValues } from '../../../../../minions/forms';
import GoToMarker from './Elements/GoToMarker';
import AppLink from '../../../../../routing/helpers/AppLink';
import Translator from '../../../../components/helpers/Translator';
import RequestForm from './Elements/RequestForm';

const NavBarLegalComplianceEvaluation = () => {
  const { moduleBridgeHex, plantHex, filtersHex } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const filterPermissions = useSelector(
    (state) =>
      state.module[moduleBridgeHex]?.permissions?.gcl?.module?.plant?.filters,
  );
  const permissions = useSelector(
    (state) => state.module[moduleBridgeHex]?.permissions,
  );
  const userBridgePermissions = useSelector(
    (state) => state.module[moduleBridgeHex]?.userBridge,
  );
  const moduleJsonSettings = useSelector(
    (s) => s.module[moduleBridgeHex]?.jsonSettings,
  );
  const [navbarInfo, setNavbarInfo] = useState();

  // > GET NARVBAR INFO
  const getNavbarInfo = async () => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_legalCompliance_evaluation_getNavbarInfo'),
        { moduleBridgeHex, plantHex },
      );
      setNavbarInfo(response.data);
    } catch (err) {}
  };

  useEffect(() => {
    getNavbarInfo();
  }, []);
  // < GET NARVBAR INFO

  // > GENERATE FILTER HEX CODE
  const generateFilterHexCode = async (values) => {
    const filters = {
      ...getGclFilterInitialValues(filterPermissions),
      ...values,
    };

    try {
      const response = await axios.post(
        axiosPath(
          'api_r_user_module_legalCompliance_evaluation_generateFilterHexCode',
        ),
        { moduleBridgeHex, filters },
      );

      navigate(
        path({
          name: 'user_module_legalCompliance_evaluation',
          params: {
            moduleBridgeHex,
            plantHex,
            page: 1,
            filtersHex: response.data.hexCode,
          },
        }),
      );
    } catch (err) {}
  };
  // < GENERATE FILTER HEX CODE

  const getExpiredBeforeThreeMonthsObligations = () => {
    generateFilterHexCode({
      calendarFrom: Moment().unix(),
      calendarTo: Moment().add(3, 'months').unix(),
    });
  };

  const getExpiredObligations = () => {
    generateFilterHexCode({
      calendarTo: Moment().unix(),
    });
  };

  const getLastThreeMonthsModifiedObligations = () => {
    generateFilterHexCode({
      modificationFrom: Moment().subtract(3, 'months').unix(),
      modificationTo: Moment().unix(),
    });
  };

  return (
    <Container>
      {permissions?.gcl?.module?.plant?.plantInfo?.request &&
        permissions?.gcl?.module?.plant?.plantInfo?.request !== false && (
          <RequestForm />
        )}
      {permissions?.gcl?.module?.userQuery &&
        permissions?.gcl?.module?.userQuery !== false && (
          <AppLink
            path="user_module_legalCompliance_userQuery_list"
            params={{
              moduleBridgeHex,
              plantHex,
            }}
            className="btn btn-sm btn-info mr-2"
          >
            <Translator
              file="module-legalCompliance"
              name="m-gcl-view.queries"
            />
          </AppLink>
        )}
      <GoToMarker />
      <NavbarIcon
        title={ReactDOMServer.renderToString(
          <Translator
            file="module-legalCompliance"
            name="deadline.less.than.three.months"
          />,
        )}
        className={
          !navbarInfo?.expiredBeforeThreeMonthsObligations ? 'off' : ''
        }
        onClick={getExpiredBeforeThreeMonthsObligations}
      >
        <AppRemixIcon
          icon="time"
          size={20}
          filled={navbarInfo?.expiredBeforeThreeMonthsObligations}
          color={
            navbarInfo?.expiredBeforeThreeMonthsObligations && colors.state2
          }
        />
        {navbarInfo?.expiredBeforeThreeMonthsObligations > 0 && (
          <>
            {navbarInfo.expiredBeforeThreeMonthsObligations > 99 ? (
              <span className="badge">+99</span>
            ) : (
              <span className="badge">
                {navbarInfo.expiredBeforeThreeMonthsObligations}
              </span>
            )}
          </>
        )}
      </NavbarIcon>
      <NavbarIcon
        title={ReactDOMServer.renderToString(
          <Translator file="module-legalCompliance" name="past.deadline" />,
        )}
        className={!navbarInfo?.expiredObligations ? 'off' : ''}
        onClick={getExpiredObligations}
      >
        <AppRemixIcon
          icon="alert"
          size={20}
          filled={navbarInfo?.expiredObligations}
          color={navbarInfo?.expiredObligations && colors.danger}
        />
        {navbarInfo?.expiredObligations > 0 && (
          <>
            {navbarInfo.expiredObligations > 99 ? (
              <span className="badge">+99</span>
            ) : (
              <span className="badge">{navbarInfo.expiredObligations}</span>
            )}
          </>
        )}
      </NavbarIcon>
      <NavbarIcon
        title={ReactDOMServer.renderToString(
          <Translator
            file="module-legalCompliance"
            name="new.obligations.latest.three.months"
          />,
        )}
        className={!navbarInfo?.lastThreeMonthsModifiedObligations ? 'off' : ''}
        onClick={getLastThreeMonthsModifiedObligations}
      >
        <AppRemixIcon
          icon="notification-2"
          size={20}
          filled={navbarInfo?.lastThreeMonthsModifiedObligations}
          color={
            navbarInfo?.lastThreeMonthsModifiedObligations &&
            colors.modifiedObligations
          }
        />
        {navbarInfo?.lastThreeMonthsModifiedObligations > 0 && (
          <>
            {navbarInfo.lastThreeMonthsModifiedObligations > 99 ? (
              <span className="badge">+99</span>
            ) : (
              <span className="badge">
                {navbarInfo.lastThreeMonthsModifiedObligations}
              </span>
            )}
          </>
        )}
      </NavbarIcon>
      {moduleJsonSettings?.calendarEmail &&
        permissions?.gcl?.module?.plant?.calendar?.customEmailNotifications &&
        userBridgePermissions?.permissions?.email && (
          <NavbarIcon
            title={ReactDOMServer.renderToString(
              <Translator file="module-legalCompliance" name="settings" />,
            )}
            className="pr-1"
          >
            <AppLink
              path="user_module_legalCompliance_plantConfiguration"
              params={{ moduleBridgeHex, plantHex }}
            >
              <AppRemixIcon icon="mail-settings" size={20} color="white" />
            </AppLink>
          </NavbarIcon>
        )}
    </Container>
  );
};

export default NavBarLegalComplianceEvaluation;

const Container = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 15px;
  color: white;
  li {
    display: inline-block;
    padding: 0 6px;
  }
  li + li {
    border-left: 2px solid ${colors.primaryLight};
  }
  li.off {
    opacity: 0.2;
  }
`;

const NavbarIcon = styled.li`
  position: relative;
  &:hover {
    cursor: pointer;
  }
  .badge {
    position: absolute;
    bottom: 0;
    right: 0;
    background-color: #f4f6f9;
    border-radius: 100px;
    color: ${colors.primary};
    font-size: 9px;
    padding: 2px;
    width: 15px;
    height: 15px;
    text-align: center;
  }
`;
